<template>
  <div
    v-if="rows.length || showReship || $slots.actions"
    class="ship-ledger"
    :class="[
      `ship-ledger--${size}`,
      hasReship ? 'is-split' : 'is-simple',
    ]"
  >
    <p v-if="hasReship" class="ship-ledger__note">
      같은 주문입니다. 아래는 송장만 추가된 재발송입니다.
    </p>

    <ul v-if="rows.length" class="ship-ledger__list">
      <li
        v-for="row in rows"
        :key="row.key"
        class="ship-ledger__row"
        :class="[
          row.kind === 'ADDITIONAL' ? 'is-reship' : 'is-main',
          { 'is-delivered': row.delivered, 'is-inflight': hasLiveStatus(row) && !row.delivered },
        ]"
      >
        <span v-if="hasReship" class="ship-ledger__label">{{ row.label }}</span>
        <button
          v-if="row.invoice"
          type="button"
          class="ship-ledger__waybill"
          @click="emit('track', row)"
        >
          {{ [row.courier, row.invoice].filter(Boolean).join(' ') }}
        </button>
        <span v-else class="ship-ledger__waybill is-plain">
          {{ row.courier || '송장 없음' }}
        </span>
        <!-- 이 송장 자체의 배송 상태를 한 눈에 보이는 배지로 뽑아 준다.
             배송완료 = 초록, 진행 중 = 파랑, 아직 조회 전이면 회색. -->
        <span
          class="ship-ledger__badge"
          :class="badgeClassOf(row)"
          :title="row.trackAt ? `택배사 최신: ${row.liveStatus}` : ''"
        >
          <AppIcon :icon="badgeIconOf(row)" :size="12" />
          {{ badgeLabelOf(row) }}
        </span>
        <button
          v-if="row.invoice"
          type="button"
          class="ship-ledger__lookup"
          :data-tour="lookupTour || undefined"
          @click="emit('track', row)"
        >
          배송조회
        </button>
        <p v-if="metaOf(row)" class="ship-ledger__meta">{{ metaOf(row) }}</p>
      </li>
    </ul>

    <div v-if="showReship || $slots.actions" class="ship-ledger__actions">
      <button
        v-if="showReship"
        type="button"
        class="sui-btn sui-btn--sm ship-ledger__reship"
        :data-tour="reshipTour"
        title="오발송·누락으로 택배를 한 번 더 보냈을 때. 주문번호는 그대로입니다."
        @click="emit('reship')"
      >
        재발송
      </button>
      <slot name="actions" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  shipmentLedgerOf,
  type ShipmentLedgerItem,
  type ShipmentLedgerRow,
} from '@/utils/mallShipment'

const props = withDefaults(
  defineProps<{
    item: ShipmentLedgerItem
    size?: 'detail' | 'compact'
    showReship?: boolean
    reshipTour?: string
    lookupTour?: string
  }>(),
  {
    size: 'detail',
    showReship: false,
    reshipTour: '',
    lookupTour: '',
  },
)

const emit = defineEmits<{
  track: [row: ShipmentLedgerRow]
  reship: []
}>()

const rows = computed(() => shipmentLedgerOf(props.item))
const hasReship = computed(() => rows.value.some((row) => row.kind === 'ADDITIONAL'))

function formatAt(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return `${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')} ${String(
    date.getHours(),
  ).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

function metaOf(row: ShipmentLedgerRow) {
  /*
   * 배송 상태는 상단 배지로 크게 표시하므로 meta 줄에는 반복하지 않는다.
   * 개수(재발송) · 사유 메모 · 발송 시각 순으로만 붙인다.
   */
  const parts: string[] = []
  if (row.kind === 'ADDITIONAL' && row.qty) {
    parts.push(`${row.qty}개`)
  }
  if (row.memo) parts.push(row.memo)
  if (row.at) parts.push(formatAt(row.at))
  return parts.join(' · ')
}

function hasLiveStatus(row: ShipmentLedgerRow) {
  return Boolean(row.liveStatus)
}

function badgeClassOf(row: ShipmentLedgerRow) {
  if (row.delivered) return 'is-delivered'
  if (hasLiveStatus(row)) return 'is-inflight'
  return 'is-unknown'
}

function badgeIconOf(row: ShipmentLedgerRow) {
  if (row.delivered) return 'mdi-check-circle'
  if (hasLiveStatus(row)) return 'mdi-truck-fast-outline'
  return 'mdi-clock-outline'
}

function badgeLabelOf(row: ShipmentLedgerRow) {
  if (row.delivered) return '배송완료'
  if (hasLiveStatus(row)) return row.liveStatus
  return '조회 전'
}
</script>

<style lang="scss" src="./MallShipmentLedger.scss"></style>
